<template>
  <!-- Компонент без своей вёрстки: окно просмотра рисует GLightbox. -->
  <span
    class="core-image-lightbox d-none"
    aria-hidden="true"
  />
</template>

<script lang="ts" setup>
/**
 * Полноэкранный просмотр изображений на базе GLightbox.
 *
 * Своей разметки окна нет — её показывает библиотека после вызова `open`.
 * Открытие: `open(index)` или `open(index, items)`.
 */
import {
  onBeforeUnmount,
  onMounted,
  ref,
} from 'vue'
import 'glightbox/dist/css/glightbox.min.css'
import type { CoreImageLightboxItem } from 'src/core/CoreImageLightbox/types'

type GLightboxInstance = {
  setElements: (_elements: Array<{
    href: string
    type: 'image'
    title?: string
    description?: string
    alt?: string
  }>) => void
  openAt: (_index: number) => void
  destroy: () => void
}

const props = defineProps<{
  items?: CoreImageLightboxItem[]
}>()

const lightboxInstance = ref<GLightboxInstance | null>(null)

onMounted(async () => {
  const GLightbox = await import('glightbox')

  lightboxInstance.value = GLightbox.default({
    touchNavigation: true,
    loop: false,
  }) as GLightboxInstance
})

/**
 * Открывает полноэкранный просмотр на изображении с индексом `index`.
 * Переданный `items` подменяет prop на этот вызов.
 */
function open(index: number, items?: CoreImageLightboxItem[]) {
  if (!lightboxInstance.value) {
    return
  }

  const preparedItems = (items ?? props.items ?? []).filter(item => Boolean(item.src))

  if (!preparedItems.length) {
    return
  }

  lightboxInstance.value.setElements(
    preparedItems.map(item => ({
      href: item.src,
      type: 'image' as const,
      title: item.title || '',
      description: '',
      alt: item.alt || item.title || '',
    })),
  )

  lightboxInstance.value.openAt(index)
}

onBeforeUnmount(() => {
  if (!lightboxInstance.value) {
    return
  }

  lightboxInstance.value.destroy()
  lightboxInstance.value = null
})

defineExpose({ open })
</script>
